import { pickBy, merge } from 'lodash-es'

import { createHttpClient } from '@sdk/utils/httpFetch'
import type { PitcherEnv } from '@sdk/interfaces'
import type { GetCanvasesParams } from '@lib/types/canvases'
import type { CanvasCreateRequest, CanvasRetrieve } from '@openapi'
import { normalizeNetworkFilterParams } from '@lib/components/filters/filter.util'

type ApiBaseUrlOverride = {
  api_base_url?: string
}

let env: PitcherEnv
let http: ReturnType<typeof createHttpClient>

function check() {
  if (!env) throw new Error('Missing env')
  if (!http) throw new Error('Missing http')
}

function pickNonNullish(data?: Record<string, any>) {
  return pickBy(data ?? {}, (v) => v !== undefined && v !== null)
}

export const setEnv = (e: PitcherEnv) => {
  if (!e?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain) {
    console.warn('No custom domain found in token claims', e?.pitcher?.token_claims)
    typeof window !== 'undefined' &&
      window.Sentry?.captureMessage?.('No custom domain found in token claims', {
        extra: { env: e },
        level: 'warning',
      })
  }

  const newCustomDomain = e?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain
  const shouldRefreshHttpClient =
    e?.pitcher?.access_token !== env?.pitcher?.access_token ||
    (newCustomDomain &&
      newCustomDomain !== env?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain)

  env = merge({}, env, e)

  if (shouldRefreshHttpClient) {
    http = createHttpClient(
      env.pitcher.token_claims['https://pitcher.com/claims/urls'].custom_domain,
      env.pitcher.access_token,
    )
  }
}

export const onlineRestApiHandlers: Record<string, any> = {
  get_canvases: async (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string } & ApiBaseUrlOverride,
  ) => {
    check()
    const { api_base_url, filters = {}, ...restParams } = params
    const res = await http.get('/canvases', {
      params: {
        instance_id: env.pitcher.instance.id,
        ...normalizeNetworkFilterParams(filters),
        ...pickNonNullish(restParams),
      },
    })

    return res
  },
  get_canvas: async (params: { id: CanvasRetrieve['id']; fields?: string }) => {
    check()
    const { id, ...restParams } = params
    const res = await http.get(`/canvases/${params.id}`, { params: restParams })
    return res
  },
  create_canvas: async (params: CanvasCreateRequest) => {
    check()
    const res = await http.post('/canvases', { ...params, instance_id: env.pitcher.instance.id })
    return res
  },
  update_canvas: async (params: { id: CanvasRetrieve['id']; fields?: string } & Record<string, any>) => {
    check()
    const { id, ...restParams } = params
    const res = await http.patch(`/canvases/${params.id}`, restParams)
    return res
  },
  delete_canvas: async (params: { id: CanvasRetrieve['id'] }) => {
    check()
    const res = await http.delete(`/canvases/${params.id}`)
    return res
  },
  share_canvas: async (params: { id: CanvasRetrieve['id'] }) => {
    check()
    let link

    try {
      link = await http.get(`/shared-links/canvas/${params.id}`)
    } catch (e) {
      if ((e as Error & { status?: number })?.status === 404) {
        link = await http.post('/shared-links', {
          canvas_id: params.id,
        })
      } else {
        throw e
      }
    }

    return link
  },
  get_canvas_recommended_files: async (params: {
    canvas_id: CanvasRetrieve['id']
    match?: ('tags' | 'metadata')[]
  }) => {
    check()
    const { canvas_id, ...restParams } = params
    const res = await http.get(`/canvases/${canvas_id}/recommended_files`, { params: restParams })
    return res
  },
  get_collaborations: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/collaborations', { params: pickNonNullish(params) })
    return res
  },
  create_collaboration: async (params: Record<string, any>) => {
    check()
    const res = await http.post('/collaborations', params)
    return res
  },
  delete_collaboration: async (params: { collaboration_id: number | string }) => {
    check()
    const { collaboration_id } = params
    const res = await http.delete(`/collaborations/${collaboration_id}`)
    return res
  },
  get_instance_memberships: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/instance-memberships', { params: pickNonNullish(params) })
    return res
  },
  get_content_distribution_groups: async (params: Record<string, any>) => {
    check()
    const res = await http.get('/content-distribution-groups', { params: pickNonNullish(params) })
    return res
  },
}
